import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import api from '@/lib/api';
import { toast } from 'sonner';
import { HardDrive, Trash2, Loader2, RefreshCw, Image, AlertTriangle, Camera } from 'lucide-react';

const VERDE = '#10B981';
const AMBAR = '#F59E0B';
const ROJO = '#DC2626';
const GRIS = '#6B7280';

export default function AdminEvidencias() {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [borrando, setBorrando] = useState(null);

  const cargar = async () => {
    setCargando(true);
    try {
      const r = await api.get('/admin/evidencias/resumen');
      setDatos(r.data);
    } catch (e) {
      toast.error('No se pudo cargar el resumen de evidencias');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const borrarAuditoria = async (a) => {
    const msg = `Vas a BORRAR PERMANENTEMENTE ${a.num_fotos} fotografia(s) de:\n\n` +
      `${a.cliente}\n${a.mb} MB\n\nEsta accion NO se puede deshacer. Las fotos ya no ` +
      `apareceran en el reporte de esa auditoria.\n\n¿Continuar?`;
    if (!window.confirm(msg)) return;
    setBorrando(a.inspection_id);
    try {
      const r = await api.delete(`/admin/evidencias/auditoria/${a.inspection_id}?confirmar=BORRAR`);
      toast.success(`${r.data.borradas} fotografia(s) eliminadas`);
      cargar();
    } catch (e) {
      toast.error('No se pudieron borrar las fotografias');
    } finally {
      setBorrando(null);
    }
  };

  const limpiarHuerfanas = async () => {
    if (!window.confirm('Vas a borrar PERMANENTEMENTE las fotografias que ya no pertenecen a ninguna auditoria.\n\n¿Continuar?')) return;
    setBorrando('huerfanas');
    try {
      const r = await api.delete('/admin/evidencias/huerfanas?confirmar=BORRAR');
      toast.success(`${r.data.borradas} archivo(s) eliminados · ${r.data.mb_liberados} MB liberados`);
      cargar();
    } catch (e) {
      toast.error('No se pudo limpiar');
    } finally {
      setBorrando(null);
    }
  };

  const esp = datos?.espacio || {};

  return (
    <AdminLayout title="Evidencias fotograficas">
      <div style={{ maxWidth: 1100 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Evidencias fotograficas</h1>
          <button onClick={cargar} disabled={cargando}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
              border: '1px solid #DDD', borderRadius: 8, background: '#FFF', cursor: 'pointer' }}>
            <RefreshCw size={15} /> Actualizar
          </button>
        </div>
        <p style={{ color: GRIS, fontSize: 14, marginTop: 0, marginBottom: 22 }}>
          Espacio ocupado por las fotografias de tus auditorias. Puedes eliminarlas de forma
          permanente para liberar espacio en el servidor.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 26 }}>
          {[
            { et: 'Fotografias', v: esp.archivos ?? 0, ic: Camera, c: '#0369A1' },
            { et: 'Espacio usado', v: (esp.mb_usados ?? 0) + ' MB', ic: HardDrive, c: AMBAR },
            { et: 'Espacio libre', v: (esp.gb_libres ?? 0) + ' GB', ic: HardDrive, c: VERDE },
            { et: 'Auditorias', v: datos?.total_auditorias ?? 0, ic: Image, c: GRIS },
          ].map((t, i) => {
            const Ic = t.ic;
            return (
              <div key={i} style={{ background: '#F8F9FA', border: '1px solid #E5E7EB',
                borderRadius: 10, padding: '16px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: GRIS, fontSize: 12, marginBottom: 8 }}>
                  <Ic size={14} /> {t.et}
                </div>
                <div style={{ fontSize: 24, fontWeight: 700, color: t.c }}>{t.v}</div>
              </div>
            );
          })}
        </div>

        {cargando ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: GRIS, padding: 40 }}>
            <Loader2 size={18} className="animate-spin" /> Cargando...
          </div>
        ) : !datos?.auditorias?.length ? (
          <div style={{ textAlign: 'center', padding: 60, color: GRIS,
            border: '1px dashed #DDD', borderRadius: 12 }}>
            <Camera size={38} style={{ opacity: 0.35, marginBottom: 12 }} />
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Sin fotografias almacenadas</div>
            <div style={{ fontSize: 13 }}>Cuando el auditor suba evidencias, apareceran aqui.</div>
          </div>
        ) : (
          <>
            <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2.4fr 1fr 0.8fr 0.8fr 0.9fr',
                background: '#F8F9FA', padding: '11px 16px', fontSize: 12, fontWeight: 700, color: GRIS }}>
                <div>CLIENTE / AUDITORIA</div><div>FECHA</div>
                <div style={{ textAlign: 'center' }}>FOTOS</div>
                <div style={{ textAlign: 'center' }}>TAMANO</div>
                <div style={{ textAlign: 'right' }}>ACCION</div>
              </div>
              {datos.auditorias.map((a) => (
                <div key={a.inspection_id} style={{ display: 'grid',
                  gridTemplateColumns: '2.4fr 1fr 0.8fr 0.8fr 0.9fr', alignItems: 'center',
                  padding: '13px 16px', borderTop: '1px solid #F0F0F0', fontSize: 13.5 }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{a.cliente}</div>
                    <div style={{ fontSize: 11.5, color: GRIS, fontFamily: 'monospace' }}>
                      {a.inspection_id?.slice(0, 8)}
                      {!a.existe_auditoria && (
                        <span style={{ marginLeft: 8, color: ROJO, fontFamily: 'inherit' }}>
                          <AlertTriangle size={11} style={{ verticalAlign: -1 }} /> auditoria eliminada
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ color: GRIS, fontSize: 12.5 }}>{(a.fecha || '').slice(0, 10)}</div>
                  <div style={{ textAlign: 'center', fontWeight: 600 }}>{a.num_fotos}</div>
                  <div style={{ textAlign: 'center', fontWeight: 600,
                    color: a.mb > 20 ? AMBAR : GRIS }}>{a.mb} MB</div>
                  <div style={{ textAlign: 'right' }}>
                    <button onClick={() => borrarAuditoria(a)} disabled={borrando === a.inspection_id}
                      title="Eliminar permanentemente"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '6px 11px', border: '1px solid ' + ROJO, borderRadius: 7,
                        background: '#FFF', color: ROJO, cursor: 'pointer', fontSize: 12.5 }}>
                      {borrando === a.inspection_id
                        ? <Loader2 size={13} className="animate-spin" />
                        : <Trash2 size={13} />} Borrar
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 22, padding: 16, background: 'rgba(245,158,11,0.07)',
              border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10 }}>
              <div style={{ fontWeight: 600, marginBottom: 5, fontSize: 14 }}>Limpieza de archivos huerfanos</div>
              <div style={{ fontSize: 13, color: GRIS, marginBottom: 12 }}>
                Elimina fotografias que quedaron en el servidor pero ya no pertenecen a ninguna
                auditoria (por ejemplo, de auditorias que borraste).
              </div>
              <button onClick={limpiarHuerfanas} disabled={borrando === 'huerfanas'}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 15px',
                  border: '1px solid ' + AMBAR, borderRadius: 8, background: '#FFF',
                  color: '#92400E', cursor: 'pointer', fontSize: 13 }}>
                {borrando === 'huerfanas'
                  ? <Loader2 size={14} className="animate-spin" />
                  : <Trash2 size={14} />} Limpiar huerfanas
              </button>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
